// client/src/components/doctors/PatientManagement/MoodAnalysisModal.jsx
import React, { useState, useEffect } from 'react';
import moodAnalyticsService from '../../../services/moodAnalyticsService';
import { formatSentimentForClinicalDisplay, getRiskLevelColor } from '../../../utils/moodUtils';
import '../../../styles/components/doctor/MoodAnalysisModal.css';

const MoodAnalysisModal = ({ isOpen, onClose, patientData }) => {
  const [moodHistory, setMoodHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [clinicalSummary, setClinicalSummary] = useState(null);

  // Fetch patient mood data when modal opens
  useEffect(() => {
    if (isOpen && patientData?.patientId) {
      fetchPatientMoodData();
    }
  }, [isOpen, patientData]);

  const fetchPatientMoodData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Fetching mood data for patient:', patientData.patientId);
      
      const response = await moodAnalyticsService.getUserMoodHistory(
        patientData.patientId,
        { 
          limit: 50, 
          page: 1, 
          includeSentiment: true 
        }
      );
      
      if (response.success) {
        setMoodHistory(response.data || []);
        setClinicalSummary(response.clinicalSummary || null);
        console.log('✅ Mood data loaded:', response.data.length, 'entries');
      } else {
        setError('Failed to load mood data');
      }
    } catch (err) {
      console.error('❌ Error fetching mood data:', err);
      setError('Error loading patient mood data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
  };

  const getRiskLevelBadge = (riskLevel) => {
    const colors = {
      POSITIVE: '#4CAF50',
      LOW: '#8BC34A',
      MILD: '#FFC107',
      MODERATE: '#FF9800',
      HIGH: '#F44336',
      CRISIS: '#D32F2F',
      UNKNOWN: '#9E9E9E'
    };

    const color = colors[riskLevel] || colors.UNKNOWN;
    
    return (
      <span 
        className="risk-badge"
        style={{ 
          backgroundColor: color,
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      >
        {riskLevel}
      </span>
    );
  };

  const getMoodEmoji = (moodKey) => {
    const emojis = {
      great: '😊',
      good: '🙂',
      okay: '😐',
      struggling: '😔',
      upset: '😢'
    };
    return emojis[moodKey] || '😐';
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderOverviewTab = () => (
    <div className="overview-tab">
      <div className="clinical-summary">
        <h3>Clinical Summary</h3>
        
        {clinicalSummary ? (
          <div className="summary-grid">
            <div className="summary-card">
              <h4>Risk Assessment</h4>
              <div className="risk-info">
                <div className="risk-level">
                  {getRiskLevelBadge(clinicalSummary.latestRiskLevel)}
                </div>
                <div className="risk-details">
                  <p>High-risk entries: {clinicalSummary.highRiskCount}</p>
                  <p>Requires attention: {clinicalSummary.requiresAttention ? 'Yes' : 'No'}</p>
                </div>
              </div>
            </div>

            <div className="summary-card">
              <h4>PHQ-9 Analysis</h4>
              <div className="phq9-stats">
                <p>Average symptoms: {clinicalSummary.phq9Statistics?.averageSymptoms || 0}</p>
                <p>Meeting criteria: {clinicalSummary.phq9Statistics?.percentMeetingCriteria || 0}%</p>
                <p>Analyzed entries: {clinicalSummary.phq9Statistics?.entriesWithPhq9 || 0}</p>
              </div>
            </div>

            <div className="summary-card">
              <h4>Mood Validation</h4>
              <div className="validation-stats">
                <p>Consistent: {clinicalSummary.validationSummary?.consistent || 0}</p>
                <p>Inconsistent: {clinicalSummary.validationSummary?.inconsistent || 0}</p>
                <p>Overridden: {clinicalSummary.validationSummary?.overridden || 0}</p>
              </div>
            </div>

            <div className="summary-card">
              <h4>Analytics Overview</h4>
              <div className="analytics-overview">
                <p>Total entries: {clinicalSummary.totalEntriesAnalyzed}</p>
                <p>Sentiment score: {clinicalSummary.averageSentimentScore}</p>
                <p>Last analyzed: {clinicalSummary.lastAnalyzed ? formatTimestamp(clinicalSummary.lastAnalyzed) : 'N/A'}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="no-summary">
            <p>No clinical summary available</p>
          </div>
        )}
      </div>

      <div className="recent-entries">
        <h3>Recent Mood Entries</h3>
        {moodHistory.length > 0 ? (
          <div className="entries-list">
            {moodHistory.slice(0, 5).map((entry, index) => (
              <div 
                key={entry._id || index} 
                className="entry-item"
                onClick={() => handleEntryClick(entry)}
              >
                <div className="entry-header">
                  <span className="mood-emoji">{getMoodEmoji(entry.moodKey)}</span>
                  <span className="mood-label">{entry.moodLabel}</span>
                  <span className="timestamp">{formatTimestamp(entry.timestamp)}</span>
                </div>
                
                {entry.sentiment && (
                  <div className="sentiment-preview">
                    {getRiskLevelBadge(entry.sentiment.riskLevel)}
                    <span className="sentiment-score">
                      {entry.sentiment.type} ({entry.sentiment.score}%)
                    </span>
                  </div>
                )}
                
                {entry.reflection && (
                  <div className="reflection-preview">
                    {entry.reflection.substring(0, 100)}
                    {entry.reflection.length > 100 ? '...' : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>No mood entries available</p>
        )}
      </div>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="history-tab">
      <h3>Complete Mood History</h3>
      
      <div className="history-filters">
        <select className="filter-select">
          <option value="all">All Entries</option>
          <option value="high-risk">High Risk Only</option>
          <option value="crisis">Crisis Only</option>
          <option value="consistent">Consistent Only</option>
          <option value="inconsistent">Inconsistent Only</option>
        </select>
      </div>

      <div className="history-list">
        {moodHistory.map((entry, index) => (
          <div 
            key={entry._id || index} 
            className={`history-entry ${entry.sentiment?.riskLevel === 'CRISIS' ? 'crisis-entry' : ''}`}
            onClick={() => handleEntryClick(entry)}
          >
            <div className="entry-main">
              <div className="entry-left">
                <span className="mood-emoji">{getMoodEmoji(entry.moodKey)}</span>
                <div className="entry-info">
                  <div className="mood-label">{entry.moodLabel}</div>
                  <div className="entry-timestamp">{formatTimestamp(entry.timestamp)}</div>
                </div>
              </div>
              
              <div className="entry-right">
                {entry.sentiment && (
                  <div className="sentiment-indicators">
                    {getRiskLevelBadge(entry.sentiment.riskLevel)}
                    <span className="sentiment-type">{entry.sentiment.type}</span>
                    <span className="sentiment-score">{entry.sentiment.score}%</span>
                  </div>
                )}
              </div>
            </div>
            
            {entry.reflection && (
              <div className="entry-reflection">
                <strong>Reflection:</strong> {entry.reflection}
              </div>
            )}
            
            {entry.sentiment && (
              <div className="entry-analysis">
                <div className="analysis-row">
                  <span className="analysis-label">Risk Level:</span>
                  <span className="analysis-value">{entry.sentiment.riskLevel}</span>
                </div>
                
                {entry.sentiment.phq9Analysis && (
                  <div className="analysis-row">
                    <span className="analysis-label">PHQ-9 Symptoms:</span>
                    <span className="analysis-value">{entry.sentiment.phq9Analysis.symptomCount}</span>
                  </div>
                )}
                
                {entry.sentiment.moodValidation && (
                  <div className="analysis-row">
                    <span className="analysis-label">Validation:</span>
                    <span className="analysis-value">{entry.sentiment.moodValidation.status}</span>
                  </div>
                )}
                
                {entry.sentiment.clinicalNotes && (
                  <div className="analysis-row">
                    <span className="analysis-label">Clinical Notes:</span>
                    <span className="analysis-value">{entry.sentiment.clinicalNotes}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="analytics-tab">
      <h3>NLP Analytics Dashboard</h3>
      
      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>Sentiment Distribution</h4>
          <div className="sentiment-chart">
            {/* Placeholder for sentiment chart */}
            <div className="chart-placeholder">
              <p>Sentiment distribution chart would go here</p>
              <p>Positive: {moodHistory.filter(e => e.sentiment?.type === 'positive').length}</p>
              <p>Neutral: {moodHistory.filter(e => e.sentiment?.type === 'neutral').length}</p>
              <p>Negative: {moodHistory.filter(e => e.sentiment?.type === 'negative').length}</p>
            </div>
          </div>
        </div>

        <div className="analytics-card">
          <h4>Risk Level Trends</h4>
          <div className="risk-trends">
            {['POSITIVE', 'LOW', 'MILD', 'MODERATE', 'HIGH', 'CRISIS'].map(level => {
              const count = moodHistory.filter(e => e.sentiment?.riskLevel === level).length;
              return (
                <div key={level} className="trend-item">
                  {getRiskLevelBadge(level)}
                  <span className="trend-count">{count} entries</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="analytics-card">
          <h4>PHQ-9 Analysis</h4>
          <div className="phq9-analysis">
            {moodHistory.filter(e => e.sentiment?.phq9Analysis).length > 0 ? (
              <div className="phq9-stats">
                <p>Entries with PHQ-9 analysis: {moodHistory.filter(e => e.sentiment?.phq9Analysis).length}</p>
                <p>Average symptoms: {
                  Math.round(
                    moodHistory
                      .filter(e => e.sentiment?.phq9Analysis)
                      .reduce((sum, e) => sum + (e.sentiment.phq9Analysis.symptomCount || 0), 0) /
                    moodHistory.filter(e => e.sentiment?.phq9Analysis).length
                  )
                }</p>
                <p>Meeting criteria: {moodHistory.filter(e => e.sentiment?.phq9Analysis?.meetsDepressionCriteria).length}</p>
              </div>
            ) : (
              <p>No PHQ-9 analysis available</p>
            )}
          </div>
        </div>

        <div className="analytics-card">
          <h4>Mood Validation</h4>
          <div className="validation-analysis">
            <div className="validation-item">
              <span className="validation-label">Consistent:</span>
              <span className="validation-count">
                {moodHistory.filter(e => e.sentiment?.moodValidation?.status === 'CONSISTENT').length}
              </span>
            </div>
            <div className="validation-item">
              <span className="validation-label">Inconsistent:</span>
              <span className="validation-count">
                {moodHistory.filter(e => e.sentiment?.moodValidation?.status === 'INCONSISTENT').length}
              </span>
            </div>
            <div className="validation-item">
              <span className="validation-label">Overridden:</span>
              <span className="validation-count">
                {moodHistory.filter(e => e.sentiment?.moodValidation?.moodOverridden).length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderRecommendationsTab = () => (
    <div className="recommendations-tab">
      <h3>Clinical Recommendations</h3>
      
      <div className="recommendations-content">
        {clinicalSummary && (
          <div className="recommendations-grid">
            <div className="recommendation-card">
              <h4>Priority Actions</h4>
              <div className="priority-actions">
                {clinicalSummary.requiresAttention && (
                  <div className="action-item high-priority">
                    <span className="action-icon">🚨</span>
                    <span className="action-text">High-risk patient requiring immediate attention</span>
                  </div>
                )}
                
                {clinicalSummary.highRiskCount > 0 && (
                  <div className="action-item medium-priority">
                    <span className="action-icon">⚠️</span>
                    <span className="action-text">{clinicalSummary.highRiskCount} high-risk entries need review</span>
                  </div>
                )}
                
                {clinicalSummary.validationSummary?.inconsistent > 0 && (
                  <div className="action-item low-priority">
                    <span className="action-icon">📋</span>
                    <span className="action-text">Review {clinicalSummary.validationSummary.inconsistent} inconsistent mood entries</span>
                  </div>
                )}
              </div>
            </div>

            <div className="recommendation-card">
              <h4>Treatment Suggestions</h4>
              <div className="treatment-suggestions">
                {clinicalSummary.phq9Statistics?.averageSymptoms > 5 && (
                  <p>• Consider depression screening - high PHQ-9 symptom count</p>
                )}
                
                {clinicalSummary.averageSentimentScore < 30 && (
                  <p>• Monitor for persistent negative mood patterns</p>
                )}
                
                {clinicalSummary.validationSummary?.overridden > 0 && (
                  <p>• Review mood validation overrides for crisis intervention effectiveness</p>
                )}
                
                <p>• Schedule follow-up appointment to discuss mood patterns</p>
                <p>• Consider increasing session frequency if risk remains high</p>
              </div>
            </div>

            <div className="recommendation-card">
              <h4>Monitoring Plan</h4>
              <div className="monitoring-plan">
                <p>• Daily mood check-ins with sentiment analysis</p>
                <p>• Weekly PHQ-9 assessment review</p>
                <p>• Immediate alerts for crisis-level entries</p>
                <p>• Monthly comprehensive mood analytics review</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="mood-analysis-modal">
        <div className="modal-header">
          <h2>Mood Analysis - {patientData?.patientName}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
            <button 
              className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
            <button 
              className={`tab-button ${activeTab === 'recommendations' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommendations')}
            >
              Recommendations
            </button>
          </div>

          <div className="tab-content">
            {isLoading && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading mood data...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>Error: {error}</p>
                <button onClick={fetchPatientMoodData}>Retry</button>
              </div>
            )}

            {!isLoading && !error && (
              <>
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'history' && renderHistoryTab()}
                {activeTab === 'analytics' && renderAnalyticsTab()}
                {activeTab === 'recommendations' && renderRecommendationsTab()}
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="secondary-button" onClick={onClose}>
            Close
          </button>
          <button className="primary-button">
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoodAnalysisModal;